import { io } from "socket.io-client";
import { toast } from "react-toastify";
import {
    assetSocketDisconnected,
    socketIsLive,
    socketIsDead,
    assetCombineData,
} from '../../features/assets/assetsSlice';
import { checkSockEvent } from '../../features/events/eventsApi';
import { socketTrackBuilt } from '../../features/datacenter/historySlice';
import { socketReportBuilt } from '../../features/datacenter/reportSlice';
import { getPdf } from '../../features/datacenter/datacenterApi';
import {
    updateSocketZone, updateSocketMarker,
    deleteSocketZone, deleteSocketMarker,
} from '../../features/places/placesSlice';

// Event buffering
let eventDataTime = null;
let sockEventBuffer = [];
let holdTime = 3000; // Keep this only for event data for now

const socketIoMiddleware = () => {
    let socket = null;
    console.log('Socket.IO Middleware initialized');

    const onConnect = store => () => {
        console.log('Socket.IO Connected');
        store.dispatch(socketIsLive());
        toast.success("LiveData Connected");
    };

    const onDisconnect = store => (reason) => {
        console.log('Socket.IO Disconnected:', reason);
        store.dispatch(socketIsDead());
        toast.error(`LiveData Disconnected`);
    };

    const onError = store => (error) => {
        console.error('Socket.IO Error:', error);
        store.dispatch(assetSocketDisconnected());
        toast.error(`LiveData Connection error`);
    };

    const onMessage = store => (data) => {
        //console.log('Socket.IO Message received:', data);
        const { activity, payload } = data;

        switch (activity) {
            case "combineData":
                //console.log('Processing combineData:', payload);
                store.dispatch(assetCombineData(payload));
                break;

            case "eventData":
                if (!eventDataTime) {
                    store.dispatch(checkSockEvent([payload]));
                    eventDataTime = new Date().getTime();
                } else {
                    if (new Date().getTime() - eventDataTime > holdTime) {
                        if (sockEventBuffer.length > 0) {
                            store.dispatch(checkSockEvent([...sockEventBuffer]));
                            sockEventBuffer = [];
                        } else {
                            store.dispatch(checkSockEvent([payload]));
                        }
                        eventDataTime = new Date().getTime();
                    } else {
                        sockEventBuffer.push(payload);
                    }
                }
                break;

            case "reportBuilt":
                if (payload.success) {
                    toast.success(`${payload.reportName} Report ready for ${payload.assetName}`);
                    if (payload.reportName === 'Track') {
                        store.dispatch(socketTrackBuilt(payload));
                    } else {
                        store.dispatch(socketReportBuilt(payload));
                    }
                } else {
                    toast.error(`${payload.assetName} Report could not be built. Please try again`);
                }
                break;

            case "pdfReportReady":
                store.dispatch(getPdf(payload.reportSummary.reportID));
                break;

            case "pdfReportEmailSent":
                toast.success(`Report emails have been sent`);
                break;

            case "errMessage":
                toast.error(payload.errMessage);
                break;

            case "zone_updated":
                toast.success("Zone updated");
                store.dispatch(updateSocketZone(payload.data_load));
                break;

            case "marker_updated":
                toast.success("Marker updated");
                store.dispatch(updateSocketMarker(payload.data_load));
                break;

            case "zone_deleted":
                toast.error("Zone deleted");
                store.dispatch(deleteSocketZone(payload));
                break;

            case "marker_deleted":
                toast.error("Marker deleted");
                store.dispatch(deleteSocketMarker(payload));
                break;

            default:
                break;
        }
    };

    return store => next => (action) => {

        switch (action.type) {
            case 'assetSocketConnect':
                if (socket) {
                    console.log('Disconnecting existing socket');
                    socket.disconnect();
                }
                if (action.payload?.host) {
                    try {
                        socket = io(action.payload.host, {
                            auth: {
                                token: action.payload.token
                            },
                            transports: ['websocket'],
                            reconnection: true,
                            reconnectionAttempts: 5,
                            reconnectionDelay: 1000,
                            reconnectionDelayMax: 5000,
                            timeout: 20000,
                            forceNew: true,
                            withCredentials: true,
                            path: '/socket.io/',
                            secure: true
                        });

                        socket.on('connect', onConnect(store));
                        socket.on('disconnect', onDisconnect(store));
                        socket.on('connect_error', onError(store));

                        socket.on('combineData', (data) => onMessage(store)(data));
                        socket.on('eventData', (data) => onMessage(store)(data));
                        socket.on('reportBuilt', (data) => onMessage(store)(data));
                        socket.on('pdfReportReady', (data) => onMessage(store)(data));
                        socket.on('pdfReportEmailSent', (data) => onMessage(store)(data));
                        socket.on('errMessage', (data) => onMessage(store)(data));
                        socket.on('zone_updated', (data) => onMessage(store)(data));
                        socket.on('marker_updated', (data) => onMessage(store)(data));
                        socket.on('zone_deleted', (data) => onMessage(store)(data));
                        socket.on('marker_deleted', (data) => onMessage(store)(data));

                        console.log('Socket.IO listeners setup complete');
                    } catch (error) {
                        console.error('Error creating Socket.IO connection:', error);
                    }
                } else {
                    console.warn('No host provided for Socket.IO connection');
                }
                break;

            case 'assetSocketDisconnect':
                console.log('Socket.IO Disconnect action received');
                if (socket) {
                    socket.disconnect();
                }
                break;

            default:
                return next(action);
        }
    };
};

export default socketIoMiddleware;
